import axios from 'axios';
import { apiRoute } from '../constants/api-constants';

// Typed wrapper over the Analytics Hub (lobbero_be/src/analytics/hub.js, mounted
// at app.use('/api', analyticsHub), so every path here is /api/analytics*).
//
// Uses the DEFAULT axios instance, like every other client in this folder, so it
// inherits the interceptors from axios-setup.ts: withCredentials for the
// httpOnly session cookies, the X-CSRF-Token double-submit header, and the
// single-flight 401 -> refresh -> retry. There is no auth code in this file and
// there must not be.
//
// buildingId / portfolioId ARE FILTERS, NOT THE BOUNDARY. Omitting both asks for
// every building the caller can see and RLS decides what that means; naming a
// portfolio can only ever NARROW, because the server intersects it with the
// buildings the caller already reads. A caller cannot widen their own answer by
// changing a parameter here.
//
// ── WHY EVERY LIST ON THIS SCREEN COMES OFF THE WIRE ──
// `meta` is a capability descriptor: the range enum, the measures, each
// measure's OWN legal dimension list, the compare enum, the severity levels and
// the visible buildings with their timezones. The types below therefore keep
// those as `string`, not as hand-written unions. A union here would be a second
// copy of a server registry, and the first time a measure gained a dimension the
// FE would either refuse a legal pair or offer an illegal one. The enum values
// are documented in the comments; they are not re-typed.

/** How a figure is bounded in time. Governs whether a delta means anything.
 *
 *  `window`  a total over [from, to). A delta is a real comparison.
 *  `as_of`   no lower bound, bounded ABOVE by the window end — the parcel shelf.
 *            The delta says "the shelf grew", which is a fact somebody asked for.
 *  `live`    as of the MOMENT OF THE QUERY, whatever window was requested. The
 *            server sends `delta: null` for these BY DESIGN: both windows would
 *            return the same number, so a delta would be a guaranteed zero
 *            presented as evidence that nothing changed. */
export type AnalyticsFigureKind = 'window' | 'as_of' | 'live';

/** `sum` for every measure but `last_activity`, whose `max_at` items carry an
 *  ISO instant in `value` instead of a count — a bar of a timestamp is
 *  meaningless, so a renderer must branch on this. */
export type AnalyticsAgg = 'sum' | 'max_at';

/**
 * THE SCOPE DESCRIPTOR — never omitted from any of the four responses.
 *
 * `enforced: false` means the organisation has scope enforcement switched off,
 * so applyScope short-circuits to org-wide for every member of staff and a
 * property manager assigned to three buildings is actually reading all twelve. A
 * dashboard captioned "your buildings" over that data is a false statement about
 * a security control, so `note` carries the sentence that has to be shown. It is
 * EMPTY for an owner, deliberately: an owner is org-wide by design, so the flag
 * says nothing about their own figures.
 */
export interface AnalyticsScope {
  /** Read from app.scope_all — the same GUC the RLS policies evaluate, not a
   *  re-derivation from the role. */
  all: boolean;
  /** The building ids this caller actually reads, and ONLY when they are not
   *  org-wide. Null beside `all: true` so a client cannot caption an org-wide
   *  answer as a narrowed one. */
  buildingIds: number[] | null;
  enforced: boolean;
  /** How many buildings the CALLER can see — the denominator. */
  buildings: number;
  /** Distinct timezones of the buildings THE ANSWER COVERS. */
  zones: string[];
  role: string;
  /** Non-empty only when it must be shown. Render it, prominently. */
  note: string;
}

/** The request's own narrowing, echoed back. `buildingIds` is non-null only for
 *  a portfolio filter, where the server resolved the portfolio into a set. */
export interface AnalyticsFocus {
  buildingId: number | null;
  portfolioId: number | null;
  buildingIds: number[] | null;
}

/**
 * THE CLOCK AN ANALYTICS ANSWER IS ON (R-T12) — on the ROOT of every hub
 * response. `tz` is the zone the server cut the window and EVERY bucket in, and
 * the zone a screen formats in; `tz_source` says whose it is: `viewer` (the
 * reader's device — every owner and manager), `building`, or `fallback` (UTC,
 * nothing usable). `zones`, by contrast, are the buildings' OWN zones.
 */
export interface AnalyticsClock {
  tz?: string;
  tz_source?: 'viewer' | 'building' | 'fallback';
  /** The window as the day keys that were asked for (source `days`). */
  fromDay?: string;
  toDay?: string;
  /** How the earlier period was chosen: by calendar, or as an equal duration. */
  compareBasis?: 'calendar' | 'duration' | null;
}

export interface AnalyticsWindow {
  from: string;
  to: string;
  tz: string;
  fromDay?: string;
  toDay?: string;
  /** 'range' | 'explicit' | 'shift' | 'last_12h'. The two shift ranges resolve
   *  from the shifts table and fall back to the last 12 hours, and "your shift"
   *  and "the last 12 hours" are different claims — so the server says which. */
  source: string;
}

/** A breakdown's own window carries no tz/source: the zone lives on the
 *  response root (`tz`) and the bounds may have been resolved PER BUILDING. */
export interface AnalyticsSpan {
  from: string | null;
  to: string | null;
}

export interface AnalyticsMeasure {
  key: string;
  label: string;
  /** [singular, plural]. Used for captions so "1 entry" is not "1 entries". */
  noun: [string, string];
  agg: AnalyticsAgg;
  /** True for `up_to` and `now` measures: they have no period, so they must not
   *  be captioned with the window or offered a comparison. */
  asOf: boolean;
  bound: string;
  /** THE MEASURE'S OWN LEGAL DIMENSION LIST. Every control on the screen is
   *  built from this. Sending a pair outside it makes the server substitute
   *  `building` and say so in `notes` — honest, but a UI that offers an
   *  impossible control is still a bug. */
  dimensions: string[];
  comparable: boolean;
  /** The overview KPI key this measure must equal when summed. The invariant
   *  that makes a tile and a chart checkable against each other. */
  sumsTo: string | null;
}

export interface AnalyticsBuilding {
  id: number;
  name: string;
  /** Its OWN zone. A calendar day starts here, not in the reader's zone. */
  timezone: string;
  portfolioId: number | null;
}

export interface AnalyticsPortfolio {
  id: number;
  name: string;
}

/* ── THE FOUR NON-COUNT PANELS, AS `meta` DESCRIBES THEM ──
 *
 * Same rule as everything above: these are capability descriptors and the FE
 * builds its controls from them. The latency subjects carry their OWN threshold
 * and their OWN bucket edges (a parcel waits days and a visitor waits hours, so
 * one shared axis would put every visitor in the first bucket), which is exactly
 * why a hard-coded subject list in the FE would offer a control the server
 * refuses or hide one it accepts. */

/** A histogram bucket EDGE, as advertised. `to: null` is the open-ended last
 *  bucket. `label` is the server's own spelling and the FE renders it verbatim:
 *  re-deriving "6-24h" from two second counts in the browser is how an axis comes
 *  to disagree with the bars above it. */
export interface AnalyticsLatencyBucket {
  label: string;
  from: number;
  to: number | null;
}

export interface AnalyticsLatencySubject {
  key: string;
  label: string;
  noun: [string, string];
  measuredFrom: string;
  measuredTo: string;
  /** The line past which a wait is a worklist item. STATED, so no copy in this
   *  repo has to name a number of its own. */
  thresholdHours: number;
  buckets: AnalyticsLatencyBucket[];
  /** The breakdown measure this subject's count must equal, or NULL when there
   *  is none. `activity` is deliberately null — no breakdown measure counts
   *  activity closes — so a client must render the honest absence rather than
   *  invent a comparison. */
  agreesWith: string | null;
}

export interface AnalyticsMetaLatency {
  subjects: AnalyticsLatencySubject[];
  defaultSubject: string;
  /** 'seconds'. The canonical unit, stated rather than assumed. */
  unit: string;
}

export interface AnalyticsMetaQuality {
  ratios: string[];
  /** The subset of `ratios` that has rows behind it, i.e. legal `?drill=` keys. */
  drills: string[];
  resolutionStates: string[];
  provenanceSources: string[];
}

export interface AnalyticsMetaTurnover {
  buckets: string[];
  defaultBucket: string;
  /** THREE: occupied | vacant | unrecorded. The FE renders three classes because
   *  the server said there are three — "we do not know" and "nobody lives there"
   *  are different facts, and merging them turns an occupancy rate into fiction. */
  occupancyClasses: string[];
}

export interface AnalyticsMetaRhythm {
  measures: string[];
  defaultMeasure: string;
  /** `hourCount`, NOT `hours`. It is a count of axis positions, not an amount of
   *  time, and a bare `hours` key on an analytics response reads as the
   *  shift-hours report this product explicitly refuses to have. Do not rename
   *  it, and do not put the words "hours worked" anywhere near it. */
  hourCount: number;
  /** 'iso' — 1 is Monday, 7 is Sunday. Postgres has both `dow` (0 = Sunday) and
   *  `isodow`, an off-by-one that silently rotates a whole heatmap, so the basis
   *  travels on the wire instead of being guessed. */
  weekdayBasis: string;
  weekdays: string[];
  cells: number;
}

export interface AnalyticsMeta {
  scope: AnalyticsScope;
  ranges: string[];
  defaultRange: string;
  compare: string[];
  defaultCompare: string;
  measures: AnalyticsMeasure[];
  defaultMeasure: string;
  dimensions: string[];
  defaultDimension: string;
  severity: string[];
  kinds: string[];
  visitorKinds: string[];
  dailyKinds: string[];
  limits: { maxPanels: number; defaultLimit: number; maxLimit: number };
  latency: AnalyticsMetaLatency;
  quality: AnalyticsMetaQuality;
  turnover: AnalyticsMetaTurnover;
  rhythm: AnalyticsMetaRhythm;
  buildings: AnalyticsBuilding[];
  portfolios: AnalyticsPortfolio[];
}

export interface AnalyticsKpi {
  value: number;
  kind: AnalyticsFigureKind;
  compareValue: number | null;
  /** Null for every `live` figure, and for any figure when compare is 'none'. */
  delta: number | null;
}

export interface AnalyticsOverview extends AnalyticsClock {
  scope: AnalyticsScope;
  focus: AnalyticsFocus;
  range: string;
  window: AnalyticsWindow;
  compare: string;
  compareWindow: AnalyticsSpan | null;
  curSpanMs: number;
  prevSpanMs: number;
  /** False means the two windows are different lengths. A percentage over an
   *  unlike pair is not a trend and must not be rendered as one. */
  likeForLike: boolean;
  /** ALWAYS false here, and stated rather than omitted: countWindow takes ONE
   *  absolute window, so unlike a breakdown's calendar range these bounds are
   *  not resolved per building. `window.tz` names the single zone they came
   *  from, and the screen has to say so when `zones.length > 1`. */
  perBuildingWindows: boolean;
  zones: string[];
  buildings: number;
  /** Keyed by read-model figure name (`entries`, `packages_pending`, …). Iterate
   *  it rather than reading a fixed list: a figure added to the read model
   *  appears here on its own. */
  kpis: Record<string, AnalyticsKpi>;
  byKind: Record<string, number>;
  compareByKind: Record<string, number> | null;
  notes: string[];
  /** The filters these tiles could NOT honour, one sentence each. Also inside
   *  `notes`; published separately so the chip row can say "…but not on these
   *  figures" without matching English out of a caption list. Empty is the
   *  normal case. */
  filterNotes?: string[];
}

export interface AnalyticsItem {
  key: string;
  label: string;
  /** A number for every `sum` measure. An ISO instant for `last_activity`
   *  (`agg: 'max_at'`), and null when that group has never recorded anything. */
  value: number | string | null;
  compareValue: number | null;
  delta: number | null;
  /** Set only for `max_at`. Same instant as `value`, named for what it is. */
  at: string | null;
}

/**
 * The engine object — what ask-facts.breakdown returns, unchanged. It is the
 * body of a /analytics/breakdown response AND of every /analytics/batch result
 * slot, so it is typed once.
 *
 * `notes` IS LOAD-BEARING AND MUST BE RENDERED. Those sentences say when a
 * dimension was substituted for one the measure has no meaning for, when only
 * non-empty groups are listed (a missing group had nothing RECORDED, which is
 * not the same as nothing happening), that a per-person split counts who WROTE
 * SOMETHING DOWN and never who was on duty, and that a cross-zone calendar range
 * was measured per building. Dropping them leaves the numbers and removes
 * everything that makes them safe to act on.
 */
export interface AnalyticsBreakdownBody {
  measure: string;
  /** What it was ACTUALLY grouped by. */
  dimension: string;
  /** What the request asked for. Different from `dimension` means the server
   *  substituted, and `notes` explains why. */
  requestedDimension: string;
  compare: string;
  label: string;
  noun: [string, string];
  agg: AnalyticsAgg;
  asOf: boolean;
  /** ALWAYS false since Plan B: every engine is handed ONE clock, so no
   *  building's period is resolved in its own zone any more. Still sent. */
  perBuildingWindows: boolean;
  zones: string[];
  /** The one clock this breakdown's buckets were cut on. */
  clockZone?: string;
  buildings: number;
  window: AnalyticsSpan;
  compareWindow: AnalyticsSpan | null;
  likeForLike: boolean;
  curSpanMs: number;
  prevSpanMs: number;
  total: number | string | null;
  compareTotal: number | null;
  delta: number | null;
  groups: number;
  items: AnalyticsItem[];
  /** True when there were more groups than `limit`, so the list is a prefix. */
  truncated: boolean;
  notes: string[];
}

/** GET /analytics/breakdown — the engine object plus what only the route knows. */
export interface AnalyticsBreakdown extends AnalyticsBreakdownBody {
  scope: AnalyticsScope;
  focus: AnalyticsFocus;
  range: string;
  requestedRange: string;
  windowSource: string;
  tz: string;
  tz_source?: 'viewer' | 'building' | 'fallback';
  fromDay?: string;
  toDay?: string;
  compareBasis?: 'calendar' | 'duration' | null;
}

/** A batch slot that answered. Note it carries NO scope/window of its own — the
 *  batch states those ONCE at the top level, because every panel shares them by
 *  construction and nine captions that could disagree is a defect. */
export type AnalyticsBatchOk = AnalyticsBreakdownBody & { id: string };

/** A batch slot that could not be answered. It is the ABSENCE of an answer and
 *  never a zero, so it renders as an error in its own tile while its siblings
 *  still render their numbers. */
export interface AnalyticsBatchFailure {
  id: string;
  error: string;
}

export type AnalyticsBatchResult = AnalyticsBatchOk | AnalyticsBatchFailure;

export const isBatchFailure = (
  result: AnalyticsBatchResult
): result is AnalyticsBatchFailure =>
  typeof (result as AnalyticsBatchFailure).error === 'string';

export interface AnalyticsBatchResponse extends AnalyticsClock {
  scope: AnalyticsScope;
  focus: AnalyticsFocus;
  range: string;
  window: AnalyticsWindow;
  compare: string;
  compareWindow: AnalyticsSpan | null;
  panels: number;
  results: AnalyticsBatchResult[];
}

/** One panel request. `id` is echoed back verbatim, so it is how a result maps
 *  to the tile that asked for it. */
export interface AnalyticsPanelRequest {
  id: string;
  measure: string;
  dimension: string;
  kinds?: string[];
  severity?: string;
  limit?: number;
}

export interface AnalyticsScopeQuery {
  range?: string;
  compare?: string;
  buildingId?: number | null;
  portfolioId?: number | null;
  /* EXPLICIT INSTANTS, WHICH EVERY HUB ROUTE ACCEPTS AND NOTHING EVER SENT.
     `from`/`to` override the range outright (hub.js resolveWindow) and must be
     given TOGETHER - half a window is refused with a 400 rather than defaulted,
     because defaulting the missing half would answer a question nobody asked and
     caption it with the one they did.

     This is what makes a custom date range, "this quarter" and "year to date"
     expressible at all: not one of the three is a member of the server's range
     enum, and all three are ordinary explicit windows. */
  from?: string | null;
  to?: string | null;
  /* DAY KEYS ('YYYY-MM-DD'), BOTH INCLUSIVE — what the screen sends for a
     picked range, a quarter, a year to date, a chart drill and a chart zoom
     (R-T5). The server resolves them midnight to midnight on the reader's
     clock: one window for every building. Both or neither; they win over
     `from` / `to`, which stay accepted for one release and which nothing here
     sends any more. */
  fromDay?: string | null;
  toDay?: string | null;

  /* ── EVERY OTHER FILTER THE SCREEN CAN SET ──
     Until this existed the scope query carried five fields and the other
     twenty-five were sent nowhere while their chips still rendered — so a
     reader could narrow to Kind = Guest, Unit 4B, 09:00–17:00 and read a chart
     of the whole organisation.

     They are OPTIONAL and dropped when empty, because the server validates each
     one against an enum and refuses an unrecognised value; an absent filter has
     to be genuinely absent. Everything the engines cannot honour for a given
     panel comes back named in that panel's `notes` rather than silently doing
     nothing. lobbero_be/src/analytics/entry-filters.js is the one definition of
     what each of these means. */
  kinds?: string[];
  severity?: string | null;
  subKind?: string | null;
  unit?: string | null;
  actors?: string[];
  role?: string | null;
  approval?: string | null;
  priority?: string | null;
  preAuthorized?: string | null;
  /** 'Yes' / 'No' — came on a visitor pass (resident-portal Plan 5). */
  onPass?: string | null;
  parkingValidation?: string | null;
  numberOfGuests?: number | null;
  carrier?: string | null;
  parcelStatus?: string | null;
  status?: string | null;
  direction?: string | null;
  groups?: string[];
  visitorType?: string | null;
  buildingActivity?: string | null;
  timeFrom?: string | null;
  timeTo?: string | null;
  q?: string | null;
  /** Sent so the server can SAY it could not apply them, never to be applied:
   *  names are encrypted with a random IV and are not expressible in SQL. */
  name?: string | null;
  lastName?: string | null;
}

export interface AnalyticsBreakdownQuery extends AnalyticsScopeQuery {
  measure?: string;
  dimension?: string;
  limit?: number;
}

/** Query serialisation. A null / empty value is DROPPED rather than sent as the
 *  string "null": the server validates every parameter against an enum and
 *  refuses an unrecognised one with a 400, so an absent filter has to be
 *  genuinely absent. */
const params = (query: AnalyticsBreakdownQuery): Record<string, string> => {
  const out: Record<string, string> = {};
  const put = (key: string, value: unknown) => {
    if (value == null || value === '') return;
    // An EMPTY ARRAY is not a filter. It joins to '', which reaches the server as
    // `kinds=` — a present-but-blank parameter that every enum check has to be
    // taught to ignore. Dropped here instead, where the shape is known.
    if (Array.isArray(value) && !value.length) return;
    out[key] = Array.isArray(value) ? value.join(',') : String(value);
  };

  put('range', query.range);
  put('compare', query.compare);
  put('from', query.from);
  put('to', query.to);
  put('from_day', query.fromDay);
  put('to_day', query.toDay);
  put('buildingId', query.buildingId);
  put('portfolioId', query.portfolioId);
  put('measure', query.measure);
  put('dimension', query.dimension);
  put('limit', query.limit);

  // The filter set. `put` drops null / '' / [] so an untouched control sends
  // nothing at all, which is what the server reads as "not asked for".
  put('kinds', query.kinds);
  put('severity', query.severity);
  put('subKind', query.subKind);
  put('unit', query.unit);
  put('actors', query.actors);
  put('role', query.role);
  put('approval', query.approval);
  put('priority', query.priority);
  put('preAuthorized', query.preAuthorized);
  put('onPass', query.onPass);
  put('parkingValidation', query.parkingValidation);
  put('numberOfGuests', query.numberOfGuests);
  put('carrier', query.carrier);
  put('parcelStatus', query.parcelStatus);
  put('status', query.status);
  put('direction', query.direction);
  put('groups', query.groups);
  put('visitorType', query.visitorType);
  put('buildingActivity', query.buildingActivity);
  put('timeFrom', query.timeFrom);
  put('timeTo', query.timeTo);
  put('q', query.q);
  put('name', query.name);
  put('lastName', query.lastName);

  return out;
};

/** The same set for a JSON body (the batch route). An empty array is dropped
 *  rather than sent as `[]`, for the same reason `put` drops an empty string. */
const bodyFilters = (query: AnalyticsScopeQuery): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  const put = (key: string, value: unknown) => {
    if (value == null || value === '') return;
    if (Array.isArray(value) && !value.length) return;
    out[key] = value;
  };

  put('kinds', query.kinds);
  put('severity', query.severity);
  put('subKind', query.subKind);
  put('unit', query.unit);
  put('actors', query.actors);
  put('role', query.role);
  put('approval', query.approval);
  put('priority', query.priority);
  put('preAuthorized', query.preAuthorized);
  put('onPass', query.onPass);
  put('parkingValidation', query.parkingValidation);
  put('numberOfGuests', query.numberOfGuests);
  put('carrier', query.carrier);
  put('parcelStatus', query.parcelStatus);
  put('status', query.status);
  put('direction', query.direction);
  put('groups', query.groups);
  put('visitorType', query.visitorType);
  put('buildingActivity', query.buildingActivity);
  put('timeFrom', query.timeFrom);
  put('timeTo', query.timeTo);
  put('q', query.q);
  put('name', query.name);
  put('lastName', query.lastName);

  return out;
};


/**
 * ABORT SUPPORT.
 *
 * Every fetcher below takes an optional `{ signal }`. The Dashboard's data
 * layer aborts a request the moment its question changes - a reader dragging a
 * range chip generates one request per stop, and each one holds a pooled
 * connection for its life against a production pool of five. An aborted request
 * frees that connection immediately instead of at the end of a query nobody is
 * waiting for.
 *
 * Optional, and additive: every existing caller passes nothing and is unchanged.
 */
export interface AnalyticsRequestOptions {
  signal?: AbortSignal;
}

export const fetchAnalyticsMeta = async (
  opts: AnalyticsRequestOptions = {}
): Promise<AnalyticsMeta> => {
  const { data } = await axios.get<AnalyticsMeta>(`${apiRoute}/analytics/meta`, {
    signal: opts.signal,
  });

  return data;
};

export const fetchAnalyticsOverview = async (
  query: AnalyticsScopeQuery = {},
  opts: AnalyticsRequestOptions = {}
): Promise<AnalyticsOverview> => {
  const { data } = await axios.get<AnalyticsOverview>(
    `${apiRoute}/analytics/overview`,
    { params: params(query), signal: opts.signal }
  );

  return data;
};

export const fetchAnalyticsBreakdown = async (
  query: AnalyticsBreakdownQuery = {},
  opts: AnalyticsRequestOptions = {}
): Promise<AnalyticsBreakdown> => {
  const { data } = await axios.get<AnalyticsBreakdown>(
    `${apiRoute}/analytics/breakdown`,
    { params: params(query), signal: opts.signal }
  );

  return data;
};

/**
 * POST /analytics/batch — the whole screen in ONE round trip.
 *
 * This is the performance design, not a convenience: the panels share one RLS
 * transaction, which is one pooled connection. Production runs a pool of 5, so
 * nine panels as nine requests is nine connections and three managers opening
 * the screen at once is the concurrency that stalled the application. The server
 * caps the array at `meta.limits.maxPanels` (12) and refuses a longer one with a
 * 400 — the caller trims, it does not discover this at runtime.
 */
export const fetchAnalyticsBatch = async (
  panels: AnalyticsPanelRequest[],
  query: AnalyticsScopeQuery = {},
  opts: AnalyticsRequestOptions = {}
): Promise<AnalyticsBatchResponse> => {
  const { data } = await axios.post<AnalyticsBatchResponse>(
    `${apiRoute}/analytics/batch`,
    {
      range: query.range,
      compare: query.compare,
      buildingId: query.buildingId ?? undefined,
      portfolioId: query.portfolioId ?? undefined,
      from: query.from ?? undefined,
      to: query.to ?? undefined,
      // At the TOP LEVEL of the body, where the route reads them.
      from_day: query.fromDay ?? undefined,
      to_day: query.toDay ?? undefined,
      // ONE FILTER SET FOR THE WHOLE BATCH. They describe the SCREEN rather than
      // a tile, so sending them per panel would invite nine panels filtered nine
      // ways under one row of chips. A panel may still narrow further with its
      // own `kinds` / `severity`, which is how the plan asks for a severity split
      // while the reader is filtered to one kind.
      ...bodyFilters(query),
      panels,
    },
    { signal: opts.signal }
  );

  return data;
};

/* ═══════════════════════════════════════════════════════════════════════════
   THE FOUR QUESTIONS A COUNT CANNOT ANSWER.
   ═══════════════════════════════════════════════════════════════════════════

   WHY THESE ARE NOT IN THE BATCH, and it is not an oversight. `/analytics/batch`
   is a batch of BREAKDOWNS: every slot is one measure grouped by one dimension,
   answered by the same engine, and its result shape is `AnalyticsBreakdownBody`.
   A percentile, a ratio-of-ratios, a move-out reason distribution and a 168-cell
   grid are none of those things, so the server serves them as four routes of
   their own. Folding them into the batch would mean a result union of five shapes
   and a discriminator the server does not send.

   They carry the SAME `scope` / `focus` / `range` / `windowSource` / `notes`
   contract as every other route on the hub, so the screen's captions, its
   enforcement warning and its cross-zone sentence all keep working unchanged.

   Each still costs its own RLS transaction, i.e. its own pooled connection — see
   the concurrency note where they are called. */

/** Every one of the four echoes this back. `notes` IS LOAD-BEARING: it is where
 *  the server says that the ageing list has no window, that an auto-close is a
 *  process gap, that the heatmap's axes bucket in each building's own zone. */
export interface AnalyticsHubEnvelope extends AnalyticsClock {
  scope: AnalyticsScope;
  focus: AnalyticsFocus;
  range: string;
  windowSource: string;
  notes: string[];
}

/** One histogram bar. `label` is the SERVER'S label, rendered verbatim — that is
 *  why it is on the wire. */
export interface AnalyticsHistogramBucket extends AnalyticsLatencyBucket {
  count: number;
}

/** One row of the ageing worklist. AS OF NOW, oldest first, and deliberately NOT
 *  filtered by the requested window: a parcel logged in March is still waiting
 *  today, and a window filter would hide exactly the ones worth chasing. */
export interface AnalyticsAgeingItem {
  id: string;
  buildingId: number;
  building: string;
  /** '' when nobody wrote a unit down — free courier text on a parcel, free desk
   *  text on a log row. An ordinary state, not a missing join. */
  unit: string;
  at: string;
  ageSeconds: number;
  ageHours: number | null;
  overThreshold: boolean;
}

/** GET /analytics/latency */
export interface AnalyticsLatency extends AnalyticsHubEnvelope {
  subject: string;
  label: string;
  noun: [string, string];
  measuredFrom: string;
  measuredTo: string;
  perBuildingWindows: boolean;
  zones: string[];
  buildings: number;
  window: AnalyticsSpan;
  /** 'seconds' — the canonical unit of every raw figure below. */
  unit: string;
  /** How many CLOSED inside the window. The denominator of `breached`, and NOT
   *  of `overThreshold`. */
  count: number;
  /** PERCENTILES IN SECONDS, and never a mean — the server does not send one.
   *  Null when nothing closed in the window. */
  p50: number | null;
  p90: number | null;
  p99: number | null;
  min: number | null;
  max: number | null;
  /** The same three percentiles in HOURS, sent so a client that reads hours does
   *  not divide by 3600 itself (and then one day by 60). */
  hoursView: { p50: number | null; p90: number | null; p99: number | null };
  thresholdHours: number;
  /** TWO NUMBERS, TWO NOUNS, and the distinction is the whole point:
   *  `breached` is a QUALITY fact about the window — this many of `count` took
   *  longer than `thresholdHours`. `overThreshold` is a WORKLIST fact about RIGHT
   *  NOW — this many of `stillOpen` are already past the line. Dividing one by
   *  the other's denominator makes both sentences false. */
  breached: number;
  /** A PERCENTAGE, 0-100, one decimal. Null when `count` is 0 — never 0%, which
   *  would read as "nothing took too long" over a window that recorded nothing. */
  breachedRatio: number | null;
  /** How many of the intervals end at a timestamp NOBODY OBSERVED: the nightly
   *  sweep's 23:59 rather than a person pressing a button. Reported rather than
   *  excluded, so a reader can size the tail it lengthens. */
  machineClosed: number;
  /** AS OF NOW. Identical across two different windows, by design. */
  stillOpen: number;
  overThreshold: number;
  histogram: AnalyticsHistogramBucket[];
  ageing: AnalyticsAgeingItem[];
  ageingTruncated: boolean;
  /** The breakdown measure this count must equal — or NULL, honestly, when no
   *  measure counts this subject's closes. Do not fabricate a comparison. */
  agreesWith: string | null;
  countWindowKey: string | null;
}

/** One of the six quality ratios. The optional fields are per-key extras the
 *  server attaches to the ratio they belong to; each is documented where it is
 *  rendered rather than being flattened into six lookalike numbers. */
export interface AnalyticsQualityRatio {
  key: string;
  label: string;
  numerator: number;
  denominator: number;
  /** A PERCENTAGE, 0-100, one decimal — already multiplied. NULL when the
   *  denominator is 0, and that null must never render as 0: "0% of nothing was
   *  scanned" reads as a failure where the truth is "nothing was recorded". */
  ratio: number | null;
  /** The key to pass back as `?drill=` to reach the offending rows, or null. */
  drill: string | null;

  /** scan_adoption: entries with NO capture record at all (pre-041, or no name
   *  field). Outside the ratio in both directions rather than counted as typed. */
  noProvenance?: number;

  /** correction_rate */
  edits?: number;
  scanOverrides?: number;
  topFields?: Array<{ field: string; count: number; fromScan: number }>;

  /** resolution_rate. `unset` and `noMatch` are OPPOSITE facts and are reported
   *  separately: unset is nobody tried, no_match is somebody tried and there is
   *  no such resident. Only the first is work. */
  unset?: number;
  noMatch?: number;
  unresolvedStates?: string[];

  /** auto_close_rate, split by source. */
  visitors?: { numerator: number; denominator: number; ratio: number | null };
  activities?: { numerator: number; denominator: number; ratio: number | null };

  /** close_rate */
  stillOpen?: number;

  /** edit_latency. p50/p90 are SECONDS and are NULL for "never corrected" — the
   *  server fixed a bug where GREATEST(0, NULL) returned 0 and produced a
   *  confident "every mistake is caught instantly". Render null as not corrected. */
  unit?: string;
  thresholdHours?: number;
  p50?: number | null;
  p90?: number | null;
  hoursView?: { p50: number | null; p90: number | null };
  source?: string;
}

/** GET /analytics/quality */
export interface AnalyticsQuality extends AnalyticsHubEnvelope {
  perBuildingWindows: boolean;
  zones: string[];
  buildings: number;
  window: AnalyticsSpan & { tz?: string };
  /** Entries CREATED in this window — every ratio's denominator comes from here. */
  entries: number;
  bySource: Record<string, number>;
  ratios: AnalyticsQualityRatio[];
}

/** GET /analytics/quality?drill=<key> — the ROWS behind one ratio. */
export interface AnalyticsQualityDrill extends AnalyticsHubEnvelope {
  drill: string;
  label: string;
  /** How many there ARE. `items` is capped; this is not. */
  matched: number;
  truncated: boolean;
  window: AnalyticsSpan & { tz?: string };
  items: Array<{
    source: string;
    id: string;
    buildingId: number;
    building: string;
    at: string;
    resolution: string;
    closeSource: string;
    closed: boolean;
  }>;
}

export interface AnalyticsTurnoverBucket {
  /** A local calendar date, YYYY-MM-DD. */
  bucket: string;
  moveIns: number;
  moveOuts: number;
  net: number;
}

/** Units as they are NOW, in three classes. `classes` is keyed by the vocabulary
 *  the server shipped, so a client renders what exists rather than what somebody
 *  typed into a chart config. */
export interface AnalyticsTurnoverOccupancy {
  buildingId: number;
  building: string;
  classes: Record<string, number>;
  units: number;
}

/** GET /analytics/turnover */
export interface AnalyticsTurnover extends AnalyticsHubEnvelope {
  bucket: string;
  /** ALWAYS false, and stated rather than omitted: moved_in / moved_out are DATE
   *  columns with no zone, so there is no building-local version of a bare date
   *  to resolve. */
  perBuildingWindows: boolean;
  zones: string[];
  /** The CALENDAR DATES the answer actually used, plus the one zone they were
   *  reduced in. */
  window: { from: string | null; to: string | null; tz: string };
  series: AnalyticsTurnoverBucket[];
  moveIns: number;
  moveOuts: number;
  net: number;
  /** '(not recorded)' is a BUCKET here, not an omission: move_out_reason is
   *  populated on a small minority of closed occupancies, so "why did they leave"
   *  is answered for the rows that have it and reported as absent for the rest. */
  reasons: Array<{ reason: string; count: number }>;
  reasonsRecorded: number;
  occupancy: AnalyticsTurnoverOccupancy[];
  classes: string[];
}

/** One cell of the 24x7 grid. All 168 are present and an empty one is a real 0. */
export interface AnalyticsRhythmCell {
  hour: number;
  /** ISO: 1 = Monday .. 7 = Sunday. */
  weekday: number;
  weekdayLabel: string;
  value: number;
}

/** GET /analytics/rhythm */
export interface AnalyticsRhythm extends AnalyticsHubEnvelope {
  measure: string;
  label: string;
  noun: [string, string];
  perBuildingWindows: boolean;
  zones: string[];
  buildings: number;
  window: AnalyticsSpan;
  weekdayBasis: string;
  weekdays: string[];
  /** `hourCount`, not `hours`. See AnalyticsMetaRhythm. */
  hourCount: number;
  cells: AnalyticsRhythmCell[];
  /** THE SCALE MAXIMUM, sent so the FE colours its ramp without a second pass
   *  over 168 cells — and so the caption and the ramp cannot disagree about which
   *  cell is busiest. */
  max: number;
  peak: AnalyticsRhythmCell | null;
  total: number;
  agreesWith: string;
}

export interface AnalyticsLatencyQuery extends AnalyticsScopeQuery {
  subject?: string;
  limit?: number;
}

export const fetchAnalyticsLatency = async (
  query: AnalyticsLatencyQuery = {},
  opts: AnalyticsRequestOptions = {}
): Promise<AnalyticsLatency> => {
  const { data } = await axios.get<AnalyticsLatency>(
    `${apiRoute}/analytics/latency`,
    {
      params: { ...params(query), ...(query.subject ? { subject: query.subject } : {}) },
      signal: opts.signal,
    }
  );

  return data;
};

export const fetchAnalyticsQuality = async (
  query: AnalyticsScopeQuery = {},
  opts: AnalyticsRequestOptions = {}
): Promise<AnalyticsQuality> => {
  const { data } = await axios.get<AnalyticsQuality>(
    `${apiRoute}/analytics/quality`,
    { params: params(query), signal: opts.signal }
  );

  return data;
};

/** The SAME route with `?drill=`, which is why it is a second function rather
 *  than a flag: the response is a different shape (rows, not ratios), and a
 *  union return type would make every caller narrow it before reading a field. */
export const fetchAnalyticsQualityDrill = async (
  drill: string,
  query: AnalyticsScopeQuery = {},
  opts: AnalyticsRequestOptions = {}
): Promise<AnalyticsQualityDrill> => {
  const { data } = await axios.get<AnalyticsQualityDrill>(
    `${apiRoute}/analytics/quality`,
    { params: { ...params(query), drill }, signal: opts.signal }
  );

  return data;
};

export interface AnalyticsTurnoverQuery extends AnalyticsScopeQuery {
  bucket?: string;
}

export const fetchAnalyticsTurnover = async (
  query: AnalyticsTurnoverQuery = {},
  opts: AnalyticsRequestOptions = {}
): Promise<AnalyticsTurnover> => {
  const { data } = await axios.get<AnalyticsTurnover>(
    `${apiRoute}/analytics/turnover`,
    {
      params: { ...params(query), ...(query.bucket ? { bucket: query.bucket } : {}) },
      signal: opts.signal,
    }
  );

  return data;
};

export interface AnalyticsRhythmQuery extends AnalyticsScopeQuery {
  measure?: string;
  // `kinds` and `severity` are on AnalyticsScopeQuery now, along with every other
  // filter the screen can set. Restating them here narrowed the type back to a
  // shape the filter object cannot produce.
}

export const fetchAnalyticsRhythm = async (
  query: AnalyticsRhythmQuery = {},
  opts: AnalyticsRequestOptions = {}
): Promise<AnalyticsRhythm> => {
  const { data } = await axios.get<AnalyticsRhythm>(
    `${apiRoute}/analytics/rhythm`,
    { params: params(query), signal: opts.signal }
  );

  return data;
};
